<template>
  <!--
    Wraps the shared AddRoleDialog (ER-12072) instead of copying it: name rules,
    Context + IQ Suggest/Stop/Re-suggest and the Replace confirmation all live there.
    This dialog only adds the attach target row, its own title/CTA copy and the
    single atomic create-draft-and-attach call (ER-12095, Roles Rule 9.1).
  -->
  <AddRoleDialog
    :value="value"
    :initial-value="initialName"
    :max-name-characters="maxNameCharacters"
    :existing-names="existingNames"
    :error-message="serverError"
    @input="onDialogInput"
    @onSave="handleSave"
    @clearError="serverError = ''"
  >
    <template #title>+ Add and attach Role</template>

    <template #before-name>
      <AddAndAttachTargetRow
        target-type-label="Position"
        :code="position.code"
        :name="position.name"
      />
    </template>

    <template #save-label>Save as Draft and Attach</template>
  </AddRoleDialog>
</template>

<script setup>
import { ref, watch } from "vue";
import AddRoleDialog from "@components/RolesCatalog/AddRoleDialog/AddRoleDialog.vue";
import AddAndAttachTargetRow from "@components/AddAndAttachTargetRow/AddAndAttachTargetRow.vue";

const props = defineProps({
  value: {
    type: Boolean,
    default: false,
  },
  position: {
    type: Object,
    required: true,
    validator: (val) => val && val.id && val.code && val.name,
  },
  initialName: {
    type: String,
    default: "",
  },
  maxNameCharacters: {
    type: Number,
    default: 120,
  },
  existingNames: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(["input", "created-and-attached"]);

const serverError = ref("");

function resolveAccountId() {
  try {
    const raw = localStorage.getItem("currentAccountID");
    if (raw == null || raw === "") return null;
    const parsed = JSON.parse(raw);
    return parsed != null ? parsed : raw;
  } catch {
    return localStorage.getItem("currentAccountID");
  }
}

function onDialogInput(nextValue) {
  if (nextValue === true) {
    emit("input", true);
    return;
  }

  serverError.value = "";
  emit("input", false);
}

async function handleSave({ name, context }) {
  const accountId = resolveAccountId();
  if (!accountId) {
    serverError.value = "Select an account to create a role.";
    return;
  }

  serverError.value = "";

  try {
    // Single atomic call: create DRAFT Role and attach to Position (ER-12095)
    const payload = {
      name,
      position_id: props.position.id,
    };

    // Include context if provided
    if (context) {
      payload.context = context;
    }

    // Resolved on save: importing the role API at the top of this dialog would pull
    // the shared axios/router module graph into every consumer of the Role picker.
    const { createDraftAndAttachToPosition } = await import("@/api/roleAPI");
    const roleResponse = await createDraftAndAttachToPosition(accountId, payload);

    emit("created-and-attached", roleResponse.data);
    emit("input", false);
  } catch (error) {
    if (error.response && error.response.data) {
      const data = error.response.data;
      if (data.name) {
        serverError.value = Array.isArray(data.name) ? data.name[0] : data.name;
      } else if (data.detail) {
        serverError.value = data.detail;
      } else {
        serverError.value = "Failed to create and attach role. Please try again.";
      }
    } else {
      serverError.value = "Failed to create and attach role. Please try again.";
    }
  }
}

watch(
  () => props.value,
  (open) => {
    if (open) serverError.value = "";
  }
);
</script>

